import { type ReactNode } from "react";
import { Info } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface TraderDetailsProfileFieldProps {
  id: string;
  label: string;
  children: ReactNode;
  errorMessage?: string;
  required?: boolean;
  tooltip?: string;
  className?: string;
}

export const TraderDetailsProfileField = ({
  id,
  label,
  children,
  errorMessage,
  required = false,
  tooltip,
  className,
}: TraderDetailsProfileFieldProps) => (
  <div className={cn("space-y-2", className)}>
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="font-semibold text-foreground">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </Label>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <WFPButton
              type="button"
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground hover:text-foreground"
              aria-label={`${label} help`}
            >
              <Info className="size-3.5" />
            </WFPButton>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} className="max-w-80">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
    {children}
    {errorMessage ? (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    ) : null}
  </div>
);
